import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { MenuList } from "./Menu";
import logo from "../../../../admin/images/logo.svg";

const isPathActive = (pathname, to) => {
  if (!to) return false;
  return pathname === to || pathname.startsWith(`${to}/`);
};

const SideBar = () => {
  const location = useLocation();
  const [isExpanded, setIsExpanded] = useState(true);
  const [isHovered, setIsHovered] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [openSubmenu, setOpenSubmenu] = useState(null);
  const [subMenuHeight, setSubMenuHeight] = useState({});
  const subMenuRefs = useRef({});

  const showText = isExpanded || isHovered || isMobileOpen;

  const isActive = useCallback(
    (path) => isPathActive(location.pathname, path),
    [location.pathname]
  );

  // Sync with existing menu-toggle class on #main-wrapper
  useEffect(() => {
    const wrapper = document.querySelector("#main-wrapper");
    if (!wrapper) return;

    const sync = () => {
      const toggled = wrapper.classList.contains("menu-toggle");
      if (window.innerWidth >= 1024) {
        // Desktop: menu-toggle = collapsed mini sidebar
        setIsExpanded(!toggled);
        setIsMobileOpen(false);
      } else {
        // Mobile: menu-toggle = drawer open (closed by default)
        setIsExpanded(true);
        setIsMobileOpen(toggled);
      }
    };

    sync();
    const observer = new MutationObserver(sync);
    observer.observe(wrapper, { attributes: true, attributeFilter: ["class"] });
    window.addEventListener("resize", sync);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", sync);
    };
  }, []);

  // Open submenu when route matches a child
  useEffect(() => {
    let matched = false;
    MenuList.forEach((nav, index) => {
      if (nav.content?.length) {
        const childActive = nav.content.some((sub) => isActive(sub.to));
        if (childActive) {
          setOpenSubmenu(index);
          matched = true;
        }
      }
    });
    if (!matched) {
      // keep open if user toggled manually — only reset when no child active
      setOpenSubmenu((prev) => {
        if (prev === null) return null;
        const nav = MenuList[prev];
        if (nav?.content?.some((sub) => isActive(sub.to))) return prev;
        return null;
      });
    }
  }, [location.pathname, isActive]);

  useEffect(() => {
    if (openSubmenu === null) return;
    const el = subMenuRefs.current[openSubmenu];
    if (el) {
      setSubMenuHeight((prev) => ({
        ...prev,
        [openSubmenu]: el.scrollHeight || 0,
      }));
    }
  }, [openSubmenu, showText]);

  const handleSubmenuToggle = (index) => {
    setOpenSubmenu((prev) => (prev === index ? null : index));
  };

  const sidebarWidth =
    isExpanded || isMobileOpen || isHovered ? "290px" : "90px";

  return (
    <>
      {isMobileOpen && (
        <div
          className="qx-sidebar-backdrop"
          onClick={() => {
            const wrapper = document.querySelector("#main-wrapper");
            if (wrapper?.classList.contains("menu-toggle")) {
              wrapper.classList.remove("menu-toggle");
            }
            setIsMobileOpen(false);
          }}
        />
      )}

      <aside
        className={`qx-sidebar ${showText ? "qx-sidebar--wide" : "qx-sidebar--mini"} ${
          isMobileOpen ? "qx-sidebar--mobile-open" : ""
        }`}
        style={{ width: sidebarWidth }}
        onMouseEnter={() => {
          if (!isExpanded && window.innerWidth >= 1024) setIsHovered(true);
        }}
        onMouseLeave={() => setIsHovered(false)}
      >
        <div className="qx-sidebar-logo">
          <Link to="/dashboard">
            {showText ? (
              <img src={logo} alt="Logo" className="qx-logo-full" />
            ) : (
              <img src={logo} alt="Logo" className="qx-logo-icon" />
            )}
          </Link>
        </div>

        <div className="qx-sidebar-scroll">
          <nav className="qx-sidebar-nav">
            <h2
              className={`qx-menu-label ${
                showText ? "qx-menu-label--start" : "qx-menu-label--center"
              }`}
            >
              {showText ? (
                "Menu"
              ) : (
                <span className="qx-menu-dots" aria-hidden>
                  ···
                </span>
              )}
            </h2>

            <ul className="qx-menu-list">
              {MenuList.map((nav, index) => {
                const hasChildren = nav.content && nav.content.length > 0;
                const childActive =
                  hasChildren &&
                  nav.content.some((sub) => isActive(sub.to));
                const itemActive = hasChildren
                  ? openSubmenu === index || childActive
                  : isActive(nav.to);
                const submenuOpen = openSubmenu === index;

                return (
                  <li key={nav.title}>
                    {hasChildren ? (
                      <>
                        <button
                          type="button"
                          className={`qx-menu-item ${
                            itemActive
                              ? "qx-menu-item--active"
                              : "qx-menu-item--inactive"
                          } ${showText ? "" : "qx-menu-item--centered"}`}
                          onClick={() => handleSubmenuToggle(index)}
                        >
                          <span
                            className={`qx-menu-icon ${
                              itemActive
                                ? "qx-menu-icon--active"
                                : "qx-menu-icon--inactive"
                            }`}
                          >
                            {nav.iconStyle}
                          </span>
                          {showText && (
                            <>
                              <span className="qx-menu-text">{nav.title}</span>
                              <svg
                                className={`qx-menu-chevron ${
                                  submenuOpen ? "qx-menu-chevron--open" : ""
                                }`}
                                width="20"
                                height="20"
                                viewBox="0 0 20 20"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  d="M5 7.5L10 12.5L15 7.5"
                                  stroke="currentColor"
                                  strokeWidth="1.5"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </svg>
                            </>
                          )}
                        </button>

                        {showText && (
                          <div
                            ref={(el) => {
                              subMenuRefs.current[index] = el;
                            }}
                            className="qx-submenu-wrap"
                            style={{
                              height: submenuOpen
                                ? `${subMenuHeight[index] || 0}px`
                                : "0px",
                            }}
                          >
                            <ul className="qx-submenu">
                              {nav.content.map((sub) => (
                                <li key={sub.title}>
                                  <Link
                                    to={sub.to}
                                    className={`qx-submenu-item ${
                                      isActive(sub.to)
                                        ? "qx-submenu-item--active"
                                        : "qx-submenu-item--inactive"
                                    }`}
                                  >
                                    {sub.title}
                                  </Link>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </>
                    ) : (
                      <Link
                        to={nav.to}
                        className={`qx-menu-item ${
                          itemActive
                            ? "qx-menu-item--active"
                            : "qx-menu-item--inactive"
                        } ${showText ? "" : "qx-menu-item--centered"}`}
                      >
                        <span
                          className={`qx-menu-icon ${
                            itemActive
                              ? "qx-menu-icon--active"
                              : "qx-menu-icon--inactive"
                          }`}
                        >
                          {nav.iconStyle}
                        </span>
                        {showText && (
                          <span className="qx-menu-text">{nav.title}</span>
                        )}
                      </Link>
                    )}
                  </li>
                );
              })}
            </ul>
          </nav>
        </div>
      </aside>
    </>
  );
};

export default SideBar;
